import React, { useEffect, useState } from 'react';
import { API_URLS } from '../config/api';
import { Link } from 'react-router-dom';
import { 
  Briefcase, 
  ShoppingCart, 
  Server, 
  MessageSquare, 
  Bot,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram
} from 'lucide-react';
import * as Icons from 'lucide-react';

const resolveIcon = (name, fallback = null) => {
  if (!name) return fallback;
  return Icons[name] || fallback;
};

const Footer = () => {
  const [settings, setSettings] = useState(null);
  const [footerSettings, setFooterSettings] = useState(null);
  const [footerMenus, setFooterMenus] = useState({
    column2: [],
    column3: [],
    column4: []
  });
  const socialDefs = [
    { name: 'Twitter', icon: Twitter, key: 'twitter' },
    { name: 'LinkedIn', icon: Linkedin, key: 'linkedin' },
    { name: 'Instagram', icon: Instagram, key: 'instagram' }
  ];

  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCompanySettings`);
        const json = await res.json();
        if (json.status === 'success') setSettings(json.data || {});
      } catch {}
      
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getFooterSettings`);
        const json = await res.json();
        if (json.status === 'success') {
          const data = json.data || {};
          // Ensure copyright_links is an array
          if (data.copyright_links) {
            if (typeof data.copyright_links === 'string') {
              try {
                data.copyright_links = JSON.parse(data.copyright_links);
              } catch (e) {
                data.copyright_links = [];
              }
            }
          } else {
            data.copyright_links = [];
          }
          setFooterSettings(data);
        }
      } catch {}
      
      try {
        const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:8000' : '';
        const res = await fetch(`${apiBase}/backend/api.php?action=getPublicMenus&type=footer`);
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await res.text();
          console.error('Footer menüleri: JSON olmayan response:', text.substring(0, 200));
          throw new Error('Response is not JSON');
        }
        
        const json = await res.json();
        if (json.status === 'success') {
          const menus = json.data || [];
          setFooterMenus({
            column2: menus.filter(m => m.footer_column === 2 || m.footer_column === '2'),
            column3: menus.filter(m => m.footer_column === 3 || m.footer_column === '3'),
            column4: menus.filter(m => m.footer_column === 4 || m.footer_column === '4')
          });
        } else {
          console.error('Footer menüleri: API error:', json.message || 'Unknown error');
        }
      } catch (e) {
        console.error('Footer menüleri yüklenemedi:', e);
        // Hata durumunda boş menü set et
        setFooterMenus({
          column2: [],
          column3: [],
          column4: []
        });
      }
    };
    load();
  }, []);

  const subscribe = async () => {
    setMsg('');
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setMsg('Geçerli bir e-posta girin');
      return;
    }
    try {
      const res = await fetch(API_URLS.NEWSLETTER_SUBSCRIBE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (res.ok) {
        setMsg(data.status === 'exists' ? 'Zaten abonesiniz' : 'Abonelik başarılı');
        setEmail('');
      } else {
        setMsg(data.error || 'Bir hata oluştu');
      }
    } catch (e) {
      setMsg('Bağlantı hatası');
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center mb-6">
              <div className="h-12 flex items-center justify-center">
                <img 
                  src={footerSettings?.logo_url || '/website-al-acik.png'} 
                  alt={footerSettings?.company_name || 'Website Al'} 
                  className="w-[200px] h-full object-contain" 
                />
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed">
              {footerSettings?.description || "Türkiye'nin en büyük B2B freelance platformu. İşletmenizin dijital dönüşümü için gereken tüm hizmetleri tek platformda bulun."}
            </p>
            <div className="space-y-2">
              <div className="flex items-start space-x-2 text-gray-400">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="break-words">{settings?.address || 'İstanbul, Türkiye'}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <Phone className="w-5 h-5 flex-shrink-0" />
                <span>{settings?.phone || '+90 212 555 0123'}</span>
              </div>
              <div className="flex items-center space-x-2 text-gray-400">
                <Mail className="w-5 h-5 flex-shrink-0" />
                <span>{settings?.email || 'info@website-al.com'}</span>
              </div>
            </div>
          </div>

          {/* Services - 2. Sütun */}
          {footerMenus.column2.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Hizmetlerimiz</h3>
              <ul className="space-y-2">
                {footerMenus.column2.map((menu) => {
                  const IconComp = resolveIcon(menu.icon, null);
                  return (
                    <li key={menu.id}>
                      <Link 
                        to={menu.url || '#'} 
                        className="flex items-center space-x-2 text-gray-400 hover:text-purple-400 transition-colors"
                      >
                        {IconComp && <IconComp className="w-4 h-4" />}
                        <span>{menu.title}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Quick Links - 3. Sütun */}
          {footerMenus.column3.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Kurumsal</h3>
              <ul className="space-y-2">
                {footerMenus.column3.map((menu) => {
                  const IconComp = resolveIcon(menu.icon, null);
                  return (
                    <li key={menu.id}>
                      <Link 
                        to={menu.url || '#'} 
                        className="flex items-center space-x-2 text-gray-400 hover:text-purple-400 transition-colors"
                      >
                        {IconComp && <IconComp className="w-4 h-4" />}
                        <span>{menu.title}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Newsletter & Social - 4. Sütun */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Bizi Takip Edin</h3>
            <p className="text-gray-400 text-sm">
              {footerSettings?.newsletter_text || 'Son güncellemeler ve özel fırsatlardan haberdar olmak için bültenimize abone olun.'}
            </p>
            <div className="flex space-x-2">
              <input 
                type="email" 
                placeholder="E-posta adresiniz"
                value={email}
                onChange={(e)=>setEmail(e.target.value)}
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <button onClick={subscribe} className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-md transition-colors">
                Abone Ol
              </button>
            </div>
            {msg && <p className="text-sm mt-2 text-gray-400">{msg}</p>}
            <div className="flex space-x-4 pt-2">
              {settings?.whatsapp && (
                <a
                  href={`https://api.whatsapp.com/send/?phone=${encodeURIComponent(settings.whatsapp)}&text=${encodeURIComponent('Merhaba, dolugopartner hakkında bilgi almak istiyorum.')}`}
                  className="text-gray-400 hover:text-green-400 transition-colors"
                  aria-label="WhatsApp"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </a>
              )}
              {socialDefs.map((s) => {
                const url = settings?.[s.key];
                if (!url) return null;
                const Icon = s.icon;
                return (
                  <a key={s.key} href={url} className="text-gray-400 hover:text-purple-400 transition-colors" aria-label={s.name}>
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              {footerSettings?.copyright_text || '© 2024 Website Al. Tüm hakları saklıdır.'}
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
              {(footerSettings?.copyright_links && Array.isArray(footerSettings.copyright_links) && footerSettings.copyright_links.length > 0
                ? footerSettings.copyright_links
                : [
                    { title: 'Gizlilik Politikası', url: '/privacy' },
                    { title: 'Kullanım Şartları', url: '/terms' },
                    { title: 'Çerez Politikası', url: '/cookies' }
                  ]
              ).map((link, index) => {
                // Parse copyright_links if it's a string (from database)
                const linkObj = typeof link === 'string' ? JSON.parse(link) : link;
                if (!linkObj || !linkObj.title || !linkObj.url) return null;
                
                return (
                  <Link key={index} to={linkObj.url} className="hover:text-purple-400 transition-colors">
                    {linkObj.title}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;